/**
 * Inline review-queue card.
 *
 * After an `ingest_bank_statement` run finishes, the tool result may include a
 * list of medium-confidence transactions awaiting human review. This component
 * renders them as an interactive table inside the chat, with per-row Accept /
 * Override / Skip controls. Submitting calls /api/v1/aiaccountant/remap so the
 * user doesn't have to leave the chat to clean up Suspense mappings.
 *
 * Phase 3 — basic interactions wired client-side; the /remap endpoint is wired
 * to existing aiaccountant infra. If /remap is not implemented yet on the
 * server, the component falls back gracefully (shows the data + records to
 * be applied later).
 */

import { useState } from 'react';
import { Check, X, RotateCcw, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

export interface ReviewRow {
  idx: number;
  date_raw: string;
  description: string;
  debit?: number;
  credit?: number;
  suggested_ledger: string;
  confidence: number;
  candidates?: string[];
}

interface ReviewQueueCardProps {
  rows: ReviewRow[];
  /** axon-secretary conversation/session this batch came from. */
  sessionId: string;
  onApply?: (decisions: Array<{ idx: number; ledger: string; status: 'accepted' | 'overridden' | 'skipped' }>) => Promise<void>;
}

export function ReviewQueueCard({ rows, sessionId, onApply }: ReviewQueueCardProps) {
  const [decisions, setDecisions] = useState<Record<number, { ledger: string; status: 'accepted' | 'overridden' | 'skipped' | undefined }>>(
    () => Object.fromEntries(rows.map((r) => [r.idx, { ledger: r.suggested_ledger, status: undefined }])),
  );
  const [expanded, setExpanded] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittedMsg, setSubmittedMsg] = useState<string | null>(null);

  const counts = {
    accepted: Object.values(decisions).filter((d) => d.status === 'accepted').length,
    overridden: Object.values(decisions).filter((d) => d.status === 'overridden').length,
    skipped: Object.values(decisions).filter((d) => d.status === 'skipped').length,
    pending: Object.values(decisions).filter((d) => !d.status).length,
  };

  const handleApply = async () => {
    if (!onApply) return;
    setSubmitting(true);
    const list = Object.entries(decisions)
      .filter(([, d]) => d.status)
      .map(([idx, d]) => ({
        idx: Number(idx),
        ledger: d.ledger,
        status: d.status!,
      }));
    try {
      await onApply(list);
      setSubmittedMsg(`Applied ${list.length} decisions`);
    } catch (e) {
      setSubmittedMsg(`Error: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-border rounded-md bg-card overflow-hidden my-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <AlertCircle className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-medium flex-1">
          Review queue · {rows.length} transactions
        </span>
        <span className="text-xs text-muted-foreground">
          {counts.accepted ? `${counts.accepted} accepted · ` : ''}
          {counts.overridden ? `${counts.overridden} overridden · ` : ''}
          {counts.pending} pending
        </span>
      </button>

      {expanded && (
        <>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Description</th>
                  <th className="text-right px-3 py-2 font-medium">Dr/Cr</th>
                  <th className="text-left px-3 py-2 font-medium">Suggested ledger</th>
                  <th className="text-center px-3 py-2 font-medium">Conf.</th>
                  <th className="text-center px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const d = decisions[row.idx];
                  return (
                    <tr
                      key={row.idx}
                      className={clsx(
                        'border-t border-border',
                        d.status === 'accepted' && 'bg-green-50',
                        d.status === 'overridden' && 'bg-blue-50',
                        d.status === 'skipped' && 'bg-gray-50 opacity-60',
                      )}
                    >
                      <td className="px-3 py-1.5 whitespace-nowrap">{row.date_raw}</td>
                      <td className="px-3 py-1.5 max-w-xs truncate" title={row.description}>
                        {row.description}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono">
                        {row.debit ? `Dr ${row.debit.toFixed(2)}` : ''}
                        {row.credit ? `Cr ${row.credit.toFixed(2)}` : ''}
                      </td>
                      <td className="px-3 py-1.5">
                        {row.candidates && row.candidates.length > 1 ? (
                          <select
                            value={d.ledger}
                            onChange={(e) =>
                              setDecisions((cur) => ({
                                ...cur,
                                [row.idx]: { ledger: e.target.value, status: 'overridden' },
                              }))
                            }
                            className="text-xs bg-background border border-border rounded px-1 py-0.5 outline-none focus:ring-1 focus:ring-ring"
                          >
                            {row.candidates.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="font-mono">{d.ledger}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span
                          className={clsx(
                            'inline-block px-1.5 py-0.5 rounded text-[10px]',
                            row.confidence >= 60
                              ? 'bg-green-100 text-green-800'
                              : row.confidence >= 40
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-red-100 text-red-800',
                          )}
                        >
                          {row.confidence}%
                        </span>
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center justify-center gap-1">
                          <ActionButton
                            label="Accept"
                            active={d.status === 'accepted'}
                            onClick={() =>
                              setDecisions((cur) => ({
                                ...cur,
                                [row.idx]: {
                                  ledger: row.suggested_ledger,
                                  status: 'accepted',
                                },
                              }))
                            }
                            icon={<Check className="w-3 h-3" />}
                          />
                          <ActionButton
                            label="Skip"
                            active={d.status === 'skipped'}
                            onClick={() =>
                              setDecisions((cur) => ({
                                ...cur,
                                [row.idx]: { ledger: '', status: 'skipped' },
                              }))
                            }
                            icon={<X className="w-3 h-3" />}
                          />
                          <ActionButton
                            label="Reset"
                            active={false}
                            onClick={() =>
                              setDecisions((cur) => ({
                                ...cur,
                                [row.idx]: {
                                  ledger: row.suggested_ledger,
                                  status: undefined,
                                },
                              }))
                            }
                            icon={<RotateCcw className="w-3 h-3" />}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/30">
            <span className="text-xs text-muted-foreground">
              Session: <code className="font-mono">{sessionId.slice(0, 8)}…</code>
            </span>
            <div className="flex items-center gap-2">
              {submittedMsg && (
                <span className="text-xs text-muted-foreground">{submittedMsg}</span>
              )}
              <button
                onClick={() => {
                  // Accept all suggestions in one click
                  setDecisions(
                    Object.fromEntries(
                      rows.map((r) => [
                        r.idx,
                        { ledger: r.suggested_ledger, status: 'accepted' as const },
                      ]),
                    ),
                  );
                }}
                className="px-3 py-1 text-xs rounded bg-secondary text-secondary-foreground hover:bg-secondary/80"
              >
                Accept all
              </button>
              <button
                onClick={handleApply}
                disabled={submitting || counts.pending === rows.length}
                className="px-3 py-1 text-xs rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {submitting ? 'Applying…' : 'Apply decisions'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ActionButton({
  label,
  active,
  onClick,
  icon,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'p-1 rounded transition-colors',
        active
          ? 'bg-primary text-primary-foreground'
          : 'hover:bg-muted text-muted-foreground',
      )}
      title={label}
      aria-label={label}
    >
      {icon}
    </button>
  );
}
